import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, IconButton, InputLabel, MenuItem,
  Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab, Tooltip, Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon, Add as AddIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { adminListBlogs, deleteBlog } from '../../api/blogs';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { AdminBlogSummary } from '../../types/blog';
import { SocialAccountsPanel } from './socialAccountsPanel';

export function BlogAdminPage() {
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'social' ? 'social' : 'posts';

  const { request: listRequest } = useApiCallWithAuth(adminListBlogs);
  const { request: deleteRequest } = useApiCallWithAuth(deleteBlog);

  function handleTabChange(tab: 'posts' | 'social') {
    const next = new URLSearchParams(searchParams);
    if (tab === 'posts') next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next);
  }

  const [blogs, setBlogs] = useState<AdminBlogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminBlogSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [domainFilter, setDomainFilter] = useState('General');

  function loadBlogs() {
    setLoading(true);
    listRequest()
      .then((data: AdminBlogSummary[]) => setBlogs(data))
      .catch(() => setError('Failed to load articles'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadBlogs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRequest(deleteTarget.blogId);
      setDeleteTarget(null);
      loadBlogs();
    } catch {
      setError('Failed to delete post');
    } finally {
      setDeleting(false);
    }
  }

  const filteredBlogs = domainFilter === 'All'
    ? blogs
    : blogs.filter(b => b.domain === domainFilter);

  return (
    <Box sx={{ pt: 3 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Typography variant='h5' fontWeight={700}>Articles Admin</Typography>
        <Stack direction='row' spacing={1}>
          {activeTab === 'posts' && (
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => navigate('/blog/new')}
            >
              New Post
            </Button>
          )}
          <Button
            variant='outlined'
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Sign Out
          </Button>
        </Stack>
      </Stack>

      <Tabs value={activeTab} onChange={(_e, value) => handleTabChange(value)} sx={{ mb: 3 }}>
        <Tab value='posts' label='Posts' />
        <Tab value='social' label='Social Accounts' />
      </Tabs>

      {activeTab === 'social' && <SocialAccountsPanel />}

      {activeTab === 'posts' && (
        <>
      <Stack direction='row' alignItems='center' sx={{ mb: 2 }}>
        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel id='domain-filter-label'>Domain</InputLabel>
          <Select
            labelId='domain-filter-label'
            value={domainFilter}
            label='Domain'
            onChange={e => setDomainFilter(e.target.value)}
          >
            <MenuItem value='All'>All</MenuItem>
            <MenuItem value='General'>General</MenuItem>
            <MenuItem value='Sports'>Sports</MenuItem>
            <MenuItem value='Running'>Running</MenuItem>
            <MenuItem value='Food'>Food</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading && <CircularProgress size={24} />}
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && !error && filteredBlogs.length === 0 && (
        <Typography color='text.secondary'>No posts yet.</Typography>
      )}

      {!loading && filteredBlogs.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Domain</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Tags</strong></TableCell>
              <TableCell align='right'><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBlogs.map(blog => (
              <TableRow key={blog.blogId} hover>
                <TableCell sx={{ maxWidth: 300 }}>
                  <Typography variant='body2' noWrap>{blog.title}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{blog.domain}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={blog.isPublished ? 'Published' : 'Draft'}
                    size='small'
                    color={blog.isPublished ? 'success' : 'default'}
                    variant={blog.isPublished ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    {new Date(blog.datePosted ? blog.datePosted + 'T00:00:00' : blog.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {blog.tags.slice(0, 3).map(tag => (
                      <Chip key={tag} label={`#${tag}`} size='small' variant='outlined' sx={{ fontSize: '0.65rem' }} />
                    ))}
                    {blog.tags.length > 3 && (
                      <Typography variant='caption' color='text.secondary'>+{blog.tags.length - 3}</Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align='right'>
                  <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
                    {blog.isPublished && (
                      <Tooltip title='View public post'>
                        <IconButton size='small' onClick={() => navigate(`/blog/${blog.blogId}`)}>
                          <VisibilityIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title='Edit'>
                      <IconButton size='small' onClick={() => navigate(`/blog/${blog.blogId}/edit`)}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete'>
                      <IconButton size='small' color='error' onClick={() => setDeleteTarget(blog)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
        </>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete post?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTarget?.title}" will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color='error' variant='contained' disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
